import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { HowToPlay, HowToPlayButton, useHowToPlay } from '@/components/games/HowToPlay';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useAccaState, useRemoveAccaPick, useSubmitAccaPick } from '@/features/games/acca';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { AccaFixture, AccaSelection } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { localizeTeamName } from '@/lib/i18n/footballNames';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { errorKey } from '@/lib/utils/errors';

const OUTCOMES: { key: AccaSelection; glyph: string }[] = [
  { key: 'home', glyph: '1' },
  { key: 'draw', glyph: 'X' },
  { key: 'away', glyph: '2' },
];

function PickButton({
  glyph,
  selected,
  isResult,
  isWrongPick,
  disabled,
  onPress,
}: {
  glyph: string;
  selected: boolean;
  isResult: boolean;
  isWrongPick: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  const palette = useThemePalette();
  // After a match finishes: the winning outcome is framed green; the user's
  // wrong pick is framed red. Before that: the selected outcome glows volt.
  const borderColor = isResult
    ? palette.win
    : isWrongPick
      ? palette.loss
      : selected
        ? palette.volt
        : palette.line;
  const bg = isResult
    ? `${palette.win}1F`
    : selected
      ? palette.voltGlassStrong
      : isWrongPick
        ? `${palette.loss}14`
        : palette.inset;
  const textColor = isResult
    ? palette.win
    : isWrongPick
      ? palette.loss
      : selected
        ? palette.voltBright
        : palette.soft;
  return (
    <Pressable
      accessibilityRole="button"
      disabled={disabled}
      onPress={onPress}
      android_ripple={{ color: 'rgba(255,255,255,0.06)' }}
      className="flex-1 items-center justify-center rounded-card border py-2.5 active:opacity-80"
      style={{ borderColor, backgroundColor: bg, opacity: disabled && !isResult && !selected && !isWrongPick ? 0.7 : 1 }}
    >
      <AppText weight="bold" tabular style={{ color: textColor, fontSize: 15 }}>
        {glyph}
      </AppText>
    </Pressable>
  );
}

function FixtureRow({ fixture }: { fixture: AccaFixture }) {
  const palette = useThemePalette();
  const { locale } = useLocale();
  const toast = useToast();
  const { t } = useTranslation();
  const submit = useSubmitAccaPick();
  const remove = useRemoveAccaPick();
  const homeName = localizeTeamName({ name: fixture.home.name }, locale);
  const awayName = localizeTeamName({ name: fixture.away.name }, locale);

  const onPick = (selection: AccaSelection) => {
    if (fixture.locked) return;
    const mutation =
      fixture.my_selection === selection
        ? remove.mutate(fixture.fixture_id, { onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }) })
        : submit.mutate(
            { fixtureId: fixture.fixture_id, selection },
            { onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }) },
          );
    return mutation;
  };

  return (
    <Card className="mx-4 gap-3 px-4 py-3.5">
      <View className="flex-row items-center justify-between">
        <View className="flex-1 flex-row items-center gap-2">
          <TeamCrest uri={fixture.home.logo} name={homeName} size="sm" />
          <AppText variant="caption" weight="semibold" className="flex-1 text-ink" numberOfLines={1}>
            {homeName}
          </AppText>
        </View>
        <AppText variant="micro" weight="bold" className="px-2 text-muted">
          {t('acca.vs')}
        </AppText>
        <View className="flex-1 flex-row items-center justify-end gap-2">
          <AppText variant="caption" weight="semibold" className="flex-1 text-right text-ink" numberOfLines={1}>
            {awayName}
          </AppText>
          <TeamCrest uri={fixture.away.logo} name={awayName} size="sm" />
        </View>
      </View>
      <View className="flex-row gap-2">
        {OUTCOMES.map((o) => (
          <PickButton
            key={o.key}
            glyph={o.glyph}
            selected={fixture.my_selection === o.key}
            isResult={fixture.result === o.key}
            isWrongPick={fixture.locked && fixture.my_selection === o.key && fixture.result != null && fixture.result !== o.key}
            disabled={fixture.locked}
            onPress={() => onPick(o.key)}
          />
        ))}
      </View>
      {fixture.locked && fixture.result == null ? (
        <View className="flex-row items-center gap-1.5">
          <Ionicons name="lock-closed" size={12} color={palette.muted} />
          <AppText variant="micro" className="text-muted">
            {t('acca.started')}
          </AppText>
        </View>
      ) : null}
    </Card>
  );
}

export default function AccaScreen() {
  const palette = useThemePalette();
  const router = useRouter();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const { userId } = useAuth();
  const howto = useHowToPlay('acca');
  const query = useAccaState();
  const state = query.data;

  const settled = state?.settled ?? null;

  return (
    <Screen>
      <HowToPlay visible={howto.visible} onClose={howto.close} i18nKey="acca" icon="layers" />
      <BackHeader
        title={t('acca.title')}
        right={
          <View className="flex-row items-center gap-2">
            <Pressable
              accessibilityRole="button"
              hitSlop={8}
              onPress={() => router.push('/games/leaderboards-hub')}
              className="active:opacity-70"
            >
              <Ionicons name="podium-outline" size={20} color={palette.voltBright} />
            </Pressable>
            <HowToPlayButton onPress={howto.open} />
          </View>
        }
      />
      <ScrollView contentContainerStyle={{ paddingBottom: 32, gap: 12 }}>
        <View className="mx-4 mb-1 flex-row items-center gap-3 rounded-card border border-volt-line bg-volt-glass px-4 py-3">
          <Ionicons name="layers" size={20} color={palette.voltBright} />
          <View className="flex-1">
            <AppText variant="caption" weight="bold" className="text-ink">
              {t('acca.subtitle')}
            </AppText>
            <AppText variant="micro" className="text-soft">
              {t('acca.howItWorks')}
            </AppText>
          </View>
        </View>

        {query.isPending ? (
          <View className="gap-3 px-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </View>
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : !state || state.fixtures.length === 0 ? (
          <EmptyState icon="layers-outline" title={t('acca.emptyTitle')} body={t('acca.emptyBody')} />
        ) : (
          <>
            {/* Jackpot / progress card */}
            <View className="mx-4 flex-row items-center gap-3 rounded-card border border-line bg-surface px-4 py-3">
              <View className="h-10 w-10 items-center justify-center rounded-full bg-volt-glass">
                <Ionicons name="trophy" size={18} color={palette.captain} />
              </View>
              <View className="flex-1">
                <AppText variant="micro" className="uppercase tracking-wide text-muted">
                  {t('acca.legsPicked', { count: state.picks_count })}
                </AppText>
                <AppText variant="caption" weight="medium" className="text-soft">
                  {state.picks_count < 3 ? t('acca.needThree') : t('acca.allMustHit')}
                </AppText>
              </View>
              <View className="items-center">
                <AppText variant="heading" weight="bold" tabular className="text-volt-bright">
                  {formatNumber(state.potential_points, numeralOptions)}
                </AppText>
                <AppText variant="micro" className="uppercase text-muted">
                  {t('acca.jackpot')}
                </AppText>
              </View>
            </View>

            {/* Settled result banner */}
            {settled ? (
              <View
                className="mx-4 flex-row items-center gap-3 rounded-card border px-4 py-3"
                style={{
                  borderColor: settled.all_correct ? palette.win : palette.loss,
                  backgroundColor: `${settled.all_correct ? palette.win : palette.loss}14`,
                }}
              >
                <Ionicons
                  name={settled.all_correct ? 'checkmark-circle' : 'close-circle'}
                  size={20}
                  color={settled.all_correct ? palette.win : palette.loss}
                />
                <AppText variant="caption" weight="bold" className="flex-1 text-ink">
                  {settled.all_correct
                    ? t('acca.won', { points: formatNumber(settled.points, numeralOptions) })
                    : t('acca.lost', {
                        hits: formatNumber(settled.hits, numeralOptions),
                        legs: formatNumber(settled.legs, numeralOptions),
                      })}
                </AppText>
              </View>
            ) : null}

            {!userId ? (
              <View className="mx-4 rounded-card border border-line bg-surface px-3.5 py-2.5">
                <AppText variant="micro" className="text-soft">
                  {t('predictions.signInToPlay')}
                </AppText>
              </View>
            ) : null}

            {state.fixtures.map((fixture) => (
              <FixtureRow key={fixture.fixture_id} fixture={fixture} />
            ))}
          </>
        )}
      </ScrollView>
    </Screen>
  );
}
